// Scheduled go/no-go decision on a Pursuit (P-L3b).
//
// This is a decision *record* — factors + due date + outcome — not a kanban
// column and not capture_projects.stage. Outcome is pending | go | defer;
// park / no-bid / loss are P-L3e and must not appear here.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useUpsertCaptureGoNoGoDecision } from './hooks';
const OUTCOME_STYLE = {
    pending: 'bg-amber-50 text-amber-800',
    go: 'bg-green-50 text-green-700',
    defer: 'bg-indigo-50 text-indigo-700'
};
const OUTCOME_LABEL = {
    pending: 'Pending',
    go: 'Go',
    defer: 'Defer'
};
function dateInputValue(ts) {
    if (!ts) return '';
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
/** Pending and the due calendar day is before today (local). */ export function isGoNoGoOverdue(outcome, dueAt) {
    if (outcome !== 'pending' || !dueAt) return false;
    const due = dueAt.toDate();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDay < today.getTime();
}
function factorsFrom(decision) {
    return (decision?.factors ?? []).map((f)=>({
            id: f.id || crypto.randomUUID(),
            name: f.name,
            body: f.body
        }));
}
export function GoNoGoPanel({ captureProjectId, decisions, correlationId }) {
    const upsert = useUpsertCaptureGoNoGoDecision(correlationId);
    const pending = useMemo(()=>decisions.find((d)=>d.outcome === 'pending') ?? null, [
        decisions
    ]);
    const latest = pending ?? decisions[0] ?? null;
    const editingPending = !latest || latest.outcome === 'pending';
    const overdue = latest ? isGoNoGoOverdue(latest.outcome, latest.dueAt) : false;
    const [factors, setFactors] = useState(()=>factorsFrom(latest ?? undefined));
    const [due, setDue] = useState(()=>dateInputValue(latest?.dueAt));
    const [notes, setNotes] = useState(()=>latest?.notes ?? '');
    // Pending: key on id/outcome so a workspace refetch does not wipe drafts.
    // Decided rows are read-only — resync notes/factors so server redaction shows.
    const formSyncKey = editingPending ? `${latest?.id ?? ''}:${latest?.outcome ?? ''}` : `${latest?.id ?? ''}:${latest?.outcome ?? ''}:${latest?.notes ?? ''}:${dateInputValue(latest?.dueAt)}:${JSON.stringify(latest?.factors ?? [])}`;
    // biome-ignore lint/correctness/useExhaustiveDependencies: formSyncKey encodes pending vs decided fields
    useEffect(()=>{
        setFactors(factorsFrom(latest ?? undefined));
        setDue(dateInputValue(latest?.dueAt));
        setNotes(latest?.notes ?? '');
    }, [
        formSyncKey
    ]);
    const persist = (outcome, id)=>upsert.mutate({
            captureProjectId,
            id: id ?? (editingPending ? latest?.id : undefined),
            factors: factors.filter((f)=>f.name.trim() || f.body.trim()),
            dueAt: parseDateInput(due),
            outcome,
            notes
        });
    const addFactor = ()=>setFactors((prev)=>[
                ...prev,
                {
                    id: crypto.randomUUID(),
                    name: '',
                    body: ''
                }
            ]);
    const removeFactor = (id)=>setFactors((prev)=>prev.filter((f)=>f.id !== id));
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.goNoGoPanel,
        className: overdue ? 'border-amber-300' : undefined,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 py-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "Go / no-go"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            overdue && /*#__PURE__*/ _jsx("span", {
                                className: "inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700",
                                "data-testid": Testids.capture.goNoGoOverdue,
                                children: "Overdue"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${OUTCOME_STYLE[latest?.outcome ?? 'pending'] ?? OUTCOME_STYLE.pending}`,
                                "data-testid": Testids.capture.goNoGoOutcome,
                                children: OUTCOME_LABEL[latest?.outcome ?? 'pending'] ?? 'Pending'
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Scheduled decision record for this pursuit — not a pipeline stage. Record Go or Defer when the meeting lands. Park / no-bid live elsewhere."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: "go-no-go-due",
                                children: "Decision meeting due"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "go-no-go-due",
                                type: "date",
                                value: due,
                                onChange: (e)=>setDue(e.target.value),
                                disabled: !editingPending || upsert.isPending,
                                "data-testid": Testids.capture.goNoGoDueInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs font-medium",
                                        children: "Factors"
                                    }),
                                    editingPending && /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: addFactor,
                                        disabled: upsert.isPending,
                                        "data-testid": Testids.capture.goNoGoAddFactor,
                                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        children: "Add factor"
                                    })
                                ]
                            }),
                            factors.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Named factors accumulate on this pursuit — vehicle fit, incumbent weakness, teaming."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-3",
                                children: factors.map((f)=>/*#__PURE__*/ _jsxs("li", {
                                        className: "space-y-2 rounded-md border p-3",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-start gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        value: f.name,
                                                        onChange: (e)=>setFactors((prev)=>prev.map((x)=>x.id === f.id ? {
                                                                        ...x,
                                                                        name: e.target.value
                                                                    } : x)),
                                                        placeholder: "Factor name",
                                                        disabled: !editingPending || upsert.isPending,
                                                        "data-testid": Testids.capture.goNoGoFactorName
                                                    }),
                                                    editingPending && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        "aria-label": "Remove factor",
                                                        onClick: ()=>removeFactor(f.id),
                                                        disabled: upsert.isPending,
                                                        children: /*#__PURE__*/ _jsx(X, {
                                                            className: "h-4 w-4"
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                value: f.body,
                                                onChange: (e)=>setFactors((prev)=>prev.map((x)=>x.id === f.id ? {
                                                                ...x,
                                                                body: e.target.value
                                                            } : x)),
                                                rows: 2,
                                                placeholder: "Why this matters for the decision",
                                                disabled: !editingPending || upsert.isPending,
                                                "data-testid": Testids.capture.goNoGoFactorBody
                                            })
                                        ]
                                    }, f.id))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: "go-no-go-notes",
                                children: "Notes"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                id: "go-no-go-notes",
                                value: notes,
                                onChange: (e)=>setNotes(e.target.value),
                                rows: 3,
                                placeholder: "Context for the decision meeting.",
                                disabled: !editingPending || upsert.isPending,
                                "data-testid": Testids.capture.goNoGoNotesInput
                            })
                        ]
                    }),
                    editingPending ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                size: "sm",
                                onClick: ()=>persist('pending'),
                                disabled: upsert.isPending,
                                "data-testid": Testids.capture.goNoGoSave,
                                children: upsert.isPending ? 'Saving…' : 'Save'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                size: "sm",
                                onClick: ()=>persist('defer'),
                                disabled: upsert.isPending,
                                "data-testid": Testids.capture.goNoGoDefer,
                                children: "Defer"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>persist('go'),
                                disabled: upsert.isPending,
                                "data-testid": Testids.capture.goNoGoRecordGo,
                                children: "Record Go"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            size: "sm",
                            onClick: ()=>persist('pending', ''),
                            disabled: upsert.isPending,
                            "data-testid": Testids.capture.goNoGoScheduleAnother,
                            children: "Schedule another decision"
                        })
                    })
                ]
            })
        ]
    });
}
